import React, { useState } from "react";
import { useLocation, useSearch } from "wouter";
import { DatabaseZap, FileText, MessageSquare, Pencil, Play, ScrollText, ThumbsDown, ThumbsUp } from "lucide-react";
import { api, fmtBytes, fmtMs, fmtUsd, invalidate, preview, refLabel, timeAgo, toast, toastError, useQuery, fmtDate } from "../lib";
import { Badge, Button, Dot, Empty, MetaChips, Modal, OutputView, Seg, SourceBadge, channelTone, JsonView } from "../ui";
import { TraceView } from "../trace";
import type { Execution } from "../../shared/types";

export function LogsTable({ project, target, onOpen, compact }: { project?: string; target?: string; onOpen: (id: string) => void; compact?: boolean }) {
  const [source, setSource] = useState<string>("");
  const q = new URLSearchParams();
  if (project) q.set("project", project);
  if (target) q.set("target", target);
  if (source) q.set("source", source);
  else q.set("hideEval", "1");
  q.set("limit", "100");
  const { data } = useQuery<Execution[]>(`/api/executions?${q}`, { poll: 5000 });
  return (
    <div className="card">
      <div className="card-head">
        <Seg value={source} onChange={setSource} options={[{ value: "", label: "API e playground" }, { value: "api", label: "API" }, { value: "playground", label: "Playground" }, { value: "eval", label: "Avaliações" }]} />
        <span className="faint mono" style={{ fontSize: 12 }}>{data?.length ?? 0} execuções</span>
      </div>
      <div className="table-wrap" style={{ borderRadius: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 20 }} />
              <th>Quando</th>
              {!target && <th>Alvo</th>}
              <th>Origem</th>
              <th>Versão</th>
              {!compact && <th>Modelo</th>}
              <th>Saída</th>
              <th className="r">Latência</th>
              <th className="r">Custo</th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((e) => (
              <tr key={e.id} className="click" onClick={() => onOpen(e.id)}>
                <td><Dot tone={e.status === "error" ? "red" : "green"} /></td>
                <td className="faint mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{timeAgo(e.createdAt)}</td>
                {!target && <td className="ellipsis" style={{ maxWidth: 160 }}>{e.target}</td>}
                <td><SourceBadge source={e.source} /></td>
                <td>{e.channel ? <Badge tone={channelTone(e.channel)}>{refLabel(e)}</Badge> : <Badge>{refLabel(e)}</Badge>}</td>
                {!compact && <td className="mono faint ellipsis" style={{ fontSize: 12, maxWidth: 170 }}>{e.model}</td>}
                <td className="ellipsis" style={{ maxWidth: 320, color: e.error ? "var(--red)" : undefined }}>{e.error ? e.error : e.outputFormat === "image" ? "imagem" : preview(e.output, 80)}</td>
                <td className="r mono" style={{ fontSize: 12 }}>{e.cacheHit ? <span style={{ color: "var(--green)" }}>cache</span> : fmtMs(e.latencyMs)}</td>
                <td className="r mono faint" style={{ fontSize: 12 }}>{fmtUsd(e.costUsd, e.costBasis)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !data.length && <Empty icon={<ScrollText size={18} />} title="Nenhuma execução">Rode no playground ou chame a API para ver o usage aqui.</Empty>}
      </div>
    </div>
  );
}

// Detalhe de uma execução (Usage), num modal grande: entrada e saída lado a lado, feedback, trace e detalhes.
export function ExecutionDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data: e } = useQuery<Execution>(id ? `/api/executions/${id}` : null);
  const [, nav] = useLocation();
  const [addOpen, setAddOpen] = useState(false);
  const [file, setFile] = useState<{ path: string; content: string | null; binary: boolean } | null>(null);
  const openFile = async (path: string) => {
    try {
      setFile(await api.get(`/api/executions/${id}/file?path=${encodeURIComponent(path)}`));
    } catch (err) {
      toastError(err);
    }
  };
  const title = e ? (
    <div className="row" style={{ gap: 8, minWidth: 0 }}>
      <Dot tone={e.status === "error" ? "red" : "green"} />
      <span className="ellipsis">{e.target}</span>
      <SourceBadge source={e.source} />
      <Badge tone={e.channel ? channelTone(e.channel) : undefined}>{refLabel(e)}</Badge>
      <div className="grow" />
      <Button size="sm" icon={<Play size={13} />} onClick={() => nav(`/p/${e.project}/t/${e.target}?ex=${e.id}`)}>Abrir no playground</Button>
      <Button size="sm" icon={<DatabaseZap size={13} />} onClick={() => setAddOpen(true)}>Virar caso</Button>
    </div>
  ) : "…";
  return (
    <>
      <Modal open={!!id} onClose={onClose} title={title} width={1180}>
        {e && (
          <>
            <div className="row wrap" style={{ gap: 6 }}><MetaChips e={e} />{e.session && <span className="chip">sessão {e.session}</span>}<span className="chip">{fmtDate(e.createdAt)}</span></div>
            <div className="ex-grid">
              <section>
                <div className="label" style={{ marginBottom: 10 }}>Entrada</div>
                <div className="col" style={{ gap: 10 }}>
                  {Object.entries(e.inputs).map(([k, v]) => (
                    <div key={k}>
                      <div className="var-name" style={{ marginBottom: 4 }}>{k}</div>
                      {typeof v === "string" && /^\/files\//.test(v) ? <img src={v} style={{ maxWidth: 260, borderRadius: 8 }} /> : typeof v === "string" ? <div className="text-out" style={{ fontSize: 13 }}>{v}</div> : <JsonView value={v} />}
                    </div>
                  ))}
                </div>
              </section>
              <section>
                <div className="label" style={{ marginBottom: 10 }}>{e.error ? "Erro" : "Saída"}</div>
                {e.error ? <div className="text-out" style={{ color: "var(--red)" }}>{e.error}</div> : <OutputView format={e.outputFormat} value={e.output} />}
              </section>
            </div>
            <ExecutionFeedback e={e} />
            {e.files && e.files.length > 0 && (
              <section>
                <div className="label" style={{ marginBottom: 10 }}>Arquivos criados ou alterados</div>
                {e.files.map((f) => (
                  <div key={f.path} className="file" onClick={() => openFile(f.path)}><FileText size={13} /><span className="name mono">{f.path}</span><span className="faint mono" style={{ fontSize: 11 }}>{fmtBytes(f.size)}</span></div>
                ))}
              </section>
            )}
            {e.trace && e.trace.length > 0 && (
              <section>
                <div className="label" style={{ marginBottom: 10 }}>Trace do agente</div>
                <TraceView events={e.trace} />
              </section>
            )}
            <section>
              <div className="label" style={{ marginBottom: 10 }}>Detalhes</div>
              <dl className="kv">
                <dt>Execução</dt><dd className="mono">{e.id}</dd>
                <dt>Modelo</dt><dd className="mono">{e.model}</dd>
                <dt>Versão</dt><dd className="mono">{e.version ? `v${e.version}` : `draft ${e.draftHash}`}</dd>
                {e.workspace && (<><dt>Workspace</dt><dd className="mono ellipsis" title={e.workspace}>{e.workspace}</dd></>)}
                {e.evalRun && (<><dt>Avaliação</dt><dd><a className="mono" href={`/p/${e.project}/e/${e.evalRun}`}>{e.evalRun}</a></dd></>)}
              </dl>
            </section>
          </>
        )}
      </Modal>
      {e && <AddCaseModal open={addOpen} onClose={() => setAddOpen(false)} exec={e} />}
      <Modal open={!!file} onClose={() => setFile(null)} title={<span className="mono">{file?.path}</span>} width={820}>
        {file?.binary ? <span className="faint">Arquivo binário.</span> : <pre className="pre" style={{ maxHeight: "65vh", overflow: "auto" }}>{file?.content}</pre>}
      </Modal>
    </>
  );
}

const FEEDBACK_KIND: Record<string, string> = { correction: "Correção", rating: "Nota", flag: "Alerta", comment: "Comentário" };
const FEEDBACK_STATUS: Record<string, string> = { open: "na Entrada", case: "virou caso", dismissed: "descartado" };

// Feedback sobre esta execução: boa, ruim, uma correção da saída ou só um comentário. Vai para a Entrada do
// projeto, de onde pode virar caso de teste.
function ExecutionFeedback({ e }: { e: Execution }) {
  const path = `/api/executions/${e.id}/feedback`;
  const { data: list } = useQuery<any[]>(path);
  const isJson = e.output !== null && typeof e.output === "object";
  const initial = e.output === null || e.output === undefined ? "" : isJson ? JSON.stringify(e.output, null, 2) : String(e.output);
  const [note, setNote] = useState("");
  const [fixing, setFixing] = useState(false);
  const [expected, setExpected] = useState(initial);
  const [busy, setBusy] = useState<string | null>(null);
  const send = async (kind: string, extra: Record<string, any> = {}) => {
    setBusy(kind + (extra.value ?? ""));
    try {
      await api.post(path, { kind, note: note.trim() || undefined, ...extra });
      invalidate(path);
      invalidate(`/api/projects/${e.project}`);
      setNote("");
      setFixing(false);
      toast("Feedback registrado", "fica na Entrada do projeto e pode virar caso de teste");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(null);
    }
  };
  const saveCorrection = () => {
    let value: any = expected;
    if (isJson) {
      try {
        value = JSON.parse(expected);
      } catch {
        toastError(new Error("A correção não é um JSON válido"));
        return;
      }
    }
    send("correction", { expected: value });
  };
  return (
    <section>
      <div className="label" style={{ marginBottom: 10 }}>Feedback</div>
      <div className="fb-compose">
        <textarea className="textarea" rows={2} value={note} onChange={(x) => setNote(x.target.value)} placeholder="O que achou desta resposta? (opcional)" />
        {fixing && (
          <div className="col" style={{ gap: 6 }}>
            <div className="faint" style={{ fontSize: 12 }}>Como a saída deveria ter sido</div>
            <textarea className="textarea mono" rows={Math.min(14, Math.max(4, expected.split("\n").length + 1))} value={expected} onChange={(x) => setExpected(x.target.value)} style={{ fontSize: 12.5 }} />
          </div>
        )}
        <div className="row" style={{ gap: 6 }}>
          <Button size="sm" icon={<ThumbsUp size={13} />} loading={busy === "rating1"} onClick={() => send("rating", { value: 1 })}>Boa</Button>
          <Button size="sm" icon={<ThumbsDown size={13} />} loading={busy === "rating-1"} onClick={() => send("rating", { value: -1 })}>Ruim</Button>
          {fixing ? (
            <>
              <Button size="sm" variant="primary" loading={busy === "correction"} disabled={!expected.trim()} onClick={saveCorrection}>Salvar correção</Button>
              <Button size="sm" variant="ghost" onClick={() => { setFixing(false); setExpected(initial); }}>Cancelar</Button>
            </>
          ) : (
            !e.error && <Button size="sm" icon={<Pencil size={13} />} onClick={() => setFixing(true)}>Corrigir saída</Button>
          )}
          <div className="grow" />
          <Button size="sm" variant="ghost" icon={<MessageSquare size={13} />} loading={busy === "comment"} disabled={!note.trim()} onClick={() => send("comment")}>Só comentar</Button>
        </div>
      </div>
      {list && list.length > 0 && (
        <div className="fb-list">
          {list.map((f) => (
            <div key={f.id} className="row" style={{ gap: 8, fontSize: 12.5, alignItems: "baseline" }}>
              <Badge tone={f.kind === "rating" ? (Number(f.value) > 0 ? "green" : "red") : f.kind === "correction" ? "blue" : "plain"}>
                {f.kind === "rating" ? (Number(f.value) > 0 ? "boa" : "ruim") : FEEDBACK_KIND[f.kind] ?? f.kind}
              </Badge>
              <span className="grow ellipsis">{f.note || (f.kind === "correction" ? preview(f.expected, 120) : "")}</span>
              <span className="faint" style={{ whiteSpace: "nowrap" }}>{f.app ?? f.source} · {timeAgo(f.createdAt)} · {FEEDBACK_STATUS[f.status] ?? f.status}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function AddCaseModal({ open, onClose, exec }: { open: boolean; onClose: () => void; exec: Execution }) {
  const [useOutput, setUseOutput] = useState(true);
  const [busy, setBusy] = useState(false);
  const path = `/api/projects/${exec.project}/targets/${exec.target}`;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`${path}/cases`, {
        id: `log-${exec.id.slice(-6)}`,
        vars: exec.inputs,
        expected: useOutput ? exec.output : undefined,
        notes: `capturado do usage ${exec.id}`,
        tags: ["capturado"],
      });
      invalidate(`${path}/cases`);
      invalidate(path);
      toast("Caso de teste adicionado", "revise o esperado na aba Avaliações antes de usar como gabarito");
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} title="Transformar esta execução em caso de teste" footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button variant="primary" loading={busy} onClick={submit}>Adicionar</Button></>}>
      <div className="muted">A entrada vira um caso de teste de <b>{exec.target}</b>. Assim um erro visto em produção passa a ser checado em toda avaliação.</div>
      <label className="check"><input type="checkbox" checked={useOutput} onChange={(e) => setUseOutput(e.target.checked)} /> Usar a saída atual como resultado esperado</label>
    </Modal>
  );
}

export function LogsPage() {
  const search = useSearch();
  const [open, setOpen] = useState<string | null>(new URLSearchParams(search).get("ex"));
  return (
    <>
      <div className="topbar"><div className="crumbs"><span className="cur">Usage</span></div></div>
      <div className="content">
        <div className="page">
          <div className="page-head"><div><h1 className="page-title">Usage</h1><div className="page-sub">Toda execução fica registrada: entrada, saída, versão, modelo, custo, latência e, nos agentes, o trace completo.</div></div></div>
          <LogsTable onOpen={setOpen} />
        </div>
      </div>
      <ExecutionDrawer id={open} onClose={() => setOpen(null)} />
    </>
  );
}
